import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FLEET = ["1 machine", "2–5 machines", "6–20 machines", "20+ machines"];
const INTEREST = ["2D — MCNAV TX73 (2D tier)", "2.5D — EasyNAV EMG100", "3D — MCNAV TX73", "Both / not sure yet"];

const inputCls =
  "w-full border border-line bg-ink px-4 py-3 text-base text-white placeholder:text-fog/50 outline-none transition-colors focus:border-signal";

const EMPTY = {
  name: "",
  company: "",
  email: "",
  phone: "",
  region: "",
  fleet_size: FLEET[0],
  interest: INTEREST[0],
  message: "",
};

function Field({ label, error, full, children }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-fog">{label}</span>
      {children}
      {error && (
        <span data-testid="demo-form-error" className="mt-1 block font-mono text-[11px] text-alert">
          {error}
        </span>
      )}
    </label>
  );
}

export default function DemoForm({
  interests = INTEREST,
  defaultInterest,
  submitLabel = "Request demo / quote",
}) {
  const [form, setForm] = useState({ ...EMPTY, interest: defaultInterest || INTEREST[0] });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!form.email.trim()) errs.email = "Required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Enter a valid email";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      await axios.post(`${API}/demo-requests`, form);
      toast.success("Request received — we'll be in touch within one business day.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Submission failed — check your connection and try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="demo-form" className="border border-line bg-surface p-6 sm:p-10">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Full name *" error={errors.name}>
          <input
            className={inputCls}
            data-testid="demo-form-name"
            value={form.name}
            onChange={set("name")}
            placeholder="Jane Doe"
          />
        </Field>
        <Field label="Company">
          <input
            className={inputCls}
            data-testid="demo-form-company"
            value={form.company}
            onChange={set("company")}
            placeholder="Doe Earthworks Ltd."
          />
        </Field>
        <Field label="Email *" error={errors.email}>
          <input
            className={inputCls}
            data-testid="demo-form-email"
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="jane@company.com"
          />
        </Field>
        <Field label="Phone">
          <input
            className={inputCls}
            data-testid="demo-form-phone"
            value={form.phone}
            onChange={set("phone")}
            placeholder="+1 (000) 000-0000"
          />
        </Field>
        <Field label="Region">
          <input
            className={inputCls}
            data-testid="demo-form-region"
            value={form.region}
            onChange={set("region")}
            placeholder="City / state"
          />
        </Field>
        <Field label="Fleet size">
          <select className={inputCls} data-testid="demo-form-fleet" value={form.fleet_size} onChange={set("fleet_size")}>
            {FLEET.map((f) => (
              <option key={f} value={f} className="bg-ink">
                {f}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Interested in" full>
          <select className={inputCls} data-testid="demo-form-interest" value={form.interest} onChange={set("interest")}>
            {interests.map((i) => (
              <option key={i} value={i} className="bg-ink">
                {i}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Project / message" full>
          <textarea
            className={`${inputCls} min-h-[110px] resize-y`}
            data-testid="demo-form-message"
            value={form.message}
            onChange={set("message")}
            placeholder="Machine makes, typical work (trenching, slopes, foundations), timeline…"
          />
        </Field>
      </div>

      <button
        type="submit"
        disabled={sending}
        data-testid="demo-form-submit-btn"
        className="mt-8 inline-flex items-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-deep disabled:cursor-not-allowed disabled:opacity-50"
      >
        {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowUpRight className="h-5 w-5" />}
        {sending ? "Sending…" : submitLabel}
      </button>
    </form>
  );
}
